import { filterDataForWeek, formatWeekRange } from '@/lib/utils/weeklyNutritionUtils'
import {
  selectCanNavigateNext,
  selectCurrentWeekEnd,
  selectCurrentWeekStart,
  selectIsCurrentWeek,
  selectIsDataLoaded,
  selectLoadingState,
  selectNutritionData,
  selectViewMode,
  useNutritionStore,
  type LoadingState,
  type WeekViewMode,
} from '@/stores/nutritionStore'
import { DailyNutrition } from '@/types/nutrition'
import { useMemo } from 'react'

/**
 * Hook to access only nutrition data from the store
 * Optimized to prevent unnecessary re-renders
 * @returns Array of DailyNutrition entries
 */
export function useNutritionData(): DailyNutrition[] {
  return useNutritionStore(selectNutritionData)
}

/**
 * Hook to access loading state from the store
 * @returns Current loading state
 */
export function useNutritionLoadingState(): LoadingState {
  return useNutritionStore(selectLoadingState)
}

/**
 * Hook to access data loaded flag from the store
 * @returns True if data has been loaded
 */
export function useIsNutritionDataLoaded(): boolean {
  return useNutritionStore(selectIsDataLoaded)
}

/**
 * Hook to access week navigation state from the store
 * Includes formatted week range for display
 * @returns Week navigation state object with formatted range
 */
export function useWeekNavigationState(): {
  currentWeekStart: Date
  currentWeekEnd: Date
  viewMode: WeekViewMode
  isCurrentWeek: boolean
  formattedWeekRange: string
  canNavigateNext: boolean
} {
  const currentWeekStart = useNutritionStore(selectCurrentWeekStart)
  const currentWeekEnd = useNutritionStore(selectCurrentWeekEnd)
  const viewMode = useNutritionStore(selectViewMode)
  const isCurrentWeek = useNutritionStore(selectIsCurrentWeek)
  const canNavigateNext = useNutritionStore(selectCanNavigateNext)

  return useMemo(
    () => ({
      currentWeekStart,
      currentWeekEnd,
      viewMode,
      isCurrentWeek,
      formattedWeekRange: formatWeekRange(currentWeekStart, currentWeekEnd),
      canNavigateNext,
    }),
    [currentWeekStart, currentWeekEnd, viewMode, isCurrentWeek, canNavigateNext]
  )
}

/**
 * Hook to access week navigation actions
 * @returns Week navigation action functions
 */
export function useWeekNavigationActions(): {
  navigatePreviousWeek: () => boolean
  navigateNextWeek: () => boolean
  navigateToCurrentWeek: () => void
  navigateToLastWeek: () => void
  navigateToRolling: () => void
  toggleViewMode: () => void
  setWeekStart: (date: Date) => void
  setViewMode: (mode: WeekViewMode) => void
} {
  const navigatePreviousWeek = useNutritionStore((state) => state.navigatePreviousWeek)
  const navigateNextWeek = useNutritionStore((state) => state.navigateNextWeek)
  const navigateToCurrentWeek = useNutritionStore((state) => state.navigateToCurrentWeek)
  const navigateToLastWeek = useNutritionStore((state) => state.navigateToLastWeek)
  const navigateToRolling = useNutritionStore((state) => state.navigateToRolling)
  const toggleViewMode = useNutritionStore((state) => state.toggleViewMode)
  const setWeekStart = useNutritionStore((state) => state.setWeekStart)
  const setViewMode = useNutritionStore((state) => state.setViewMode)

  return useMemo(
    () => ({
      navigatePreviousWeek,
      navigateNextWeek,
      navigateToCurrentWeek,
      navigateToLastWeek,
      navigateToRolling,
      toggleViewMode,
      setWeekStart,
      setViewMode,
    }),
    [
      navigatePreviousWeek,
      navigateNextWeek,
      navigateToCurrentWeek,
      navigateToLastWeek,
      navigateToRolling,
      toggleViewMode,
      setWeekStart,
      setViewMode,
    ]
  )
}

/**
 * Hook to access data management actions
 * @returns Data management action functions
 */
export function useNutritionDataActions(): {
  loadData: () => void
  loadDefaultData: () => Promise<void>
  setNutritionData: (data: DailyNutrition[]) => void
  addNutritionEntry: (entry: DailyNutrition) => void
  clearData: () => void
  resetError: () => void
} {
  const loadData = useNutritionStore((state) => state.loadData)
  const loadDefaultData = useNutritionStore((state) => state.loadDefaultData)
  const setNutritionData = useNutritionStore((state) => state.setNutritionData)
  const addNutritionEntry = useNutritionStore((state) => state.addNutritionEntry)
  const clearData = useNutritionStore((state) => state.clearData)
  const resetError = useNutritionStore((state) => state.resetError)

  return useMemo(
    () => ({
      loadData,
      loadDefaultData,
      setNutritionData,
      addNutritionEntry,
      clearData,
      resetError,
    }),
    [loadData, loadDefaultData, setNutritionData, addNutritionEntry, clearData, resetError]
  )
}

/**
 * Hook to get filtered nutrition data for the current week
 * Automatically recalculates when nutrition data or week dates change
 * @returns Nutrition data filtered for current week
 */
export function useWeeklyNutritionData(): readonly DailyNutrition[] {
  const nutritionData = useNutritionData()
  const { currentWeekStart, currentWeekEnd } = useWeekNavigationState()

  return useMemo(() => {
    return filterDataForWeek(nutritionData, currentWeekStart, currentWeekEnd)
  }, [nutritionData, currentWeekStart, currentWeekEnd])
}

/**
 * Combined hook that returns both week navigation state and actions
 * Use this when you need everything from week navigation
 * @returns Combined week navigation state and actions with formatted range
 */
export function useWeekNavigation(): {
  // State
  currentWeekStart: Date
  currentWeekEnd: Date
  viewMode: WeekViewMode
  isCurrentWeek: boolean
  formattedWeekRange: string
  canNavigateNext: boolean
  // Actions
  navigatePreviousWeek: () => boolean
  navigateNextWeek: () => boolean
  navigateToCurrentWeek: () => void
  navigateToLastWeek: () => void
  navigateToRolling: () => void
  toggleViewMode: () => void
  setWeekStart: (date: Date) => void
  setViewMode: (mode: WeekViewMode) => void
} {
  const state = useWeekNavigationState()
  const actions = useWeekNavigationActions()

  return useMemo(
    () => ({
      ...state,
      ...actions,
    }),
    [state, actions]
  )
}

/**
 * Combined hook that returns all nutrition store state and actions
 * Use this when you need full control over the store
 * @returns Full nutrition store state and actions
 */
export function useNutritionStoreAll(): {
  // Data state
  nutritionData: readonly DailyNutrition[]
  loadingState: LoadingState
  error: Error | null
  isDataLoaded: boolean
  // Week navigation state
  currentWeekStart: Date
  currentWeekEnd: Date
  viewMode: WeekViewMode
  isCurrentWeek: boolean
  // Data actions
  loadData: () => void
  loadDefaultData: () => Promise<void>
  setNutritionData: (data: DailyNutrition[]) => void
  addNutritionEntry: (entry: DailyNutrition) => void
  clearData: () => void
  resetError: () => void
  // Week navigation actions
  navigatePreviousWeek: () => boolean
  navigateNextWeek: () => boolean
  navigateToCurrentWeek: () => void
  navigateToLastWeek: () => void
  navigateToRolling: () => void
  toggleViewMode: () => void
  setWeekStart: (date: Date) => void
  setViewMode: (mode: WeekViewMode) => void
} {
  // Data state
  const nutritionData = useNutritionData()
  const loadingState = useNutritionLoadingState()
  const error = useNutritionStore((state) => state.error)
  const isDataLoaded = useIsNutritionDataLoaded()

  // Week navigation state
  const weekState = useWeekNavigationState()

  // Actions
  const dataActions = useNutritionDataActions()
  const weekActions = useWeekNavigationActions()

  return useMemo(
    () => ({
      // Data state
      nutritionData,
      loadingState,
      error,
      isDataLoaded,
      // Week navigation state
      ...weekState,
      // All actions
      ...dataActions,
      ...weekActions,
    }),
    [nutritionData, loadingState, error, isDataLoaded, weekState, dataActions, weekActions]
  )
}
